import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Loader2, Trophy, RefreshCw, ChevronRight, Users2, Save, Lock, Eye,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  afriListTasks, afriRankTask, afriRankingStatus, afriTaskResults,
  afriAcceptTask, afriUpdateTaskConfig, afriLockTask,
} from "@/lib/afritask";

const TABS = [
  { key: "awaiting_ranking,locked", label: "Awaiting Rank" },
  { key: "ranked", label: "Ranked" },
  { key: "finalized", label: "Finalized" },
];

export default function AdminAfriSelection() {
  const navigate = useNavigate();
  const initialTab = (() => {
    const p = new URLSearchParams(window.location.search).get("tab");
    if (p === "ranked") return "ranked";
    if (p === "finalized") return "finalized";
    return TABS[0].key;
  })();
  const [tab, setTab] = useState(initialTab);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rankTaskModal, setRankTaskModal] = useState(null); // task pending config confirm + ranking
  const [rankProgress, setRankProgress] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [detail, setDetail] = useState(null);
  const [acceptBusy, setAcceptBusy] = useState(false);
  const [lockTarget, setLockTarget] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);

  // Overrides for winners_count + min_winning_score in the rank modal + results view
  const [overrideWinners, setOverrideWinners] = useState("");
  const [overrideMin, setOverrideMin] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await afriListTasks(tab);
      setTasks(r.tasks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Poll ranking status. `rankProgress.job` is populated once ranking starts.
  useEffect(() => {
    if (!rankTaskModal || !rankProgress?.running) return;
    const id = setInterval(async () => {
      try {
        const s = await afriRankingStatus(rankTaskModal.task_id);
        setRankProgress((p) => ({ ...p, job: s.job, task_status: s.task_status }));
        if (s.task_status === "ranked" || s.job?.status === "done") {
          clearInterval(id);
          toast.success(`Ranking complete — top score ${s.ai_top_score}, avg ${s.ai_avg_score}`);
          setRankTaskModal(null);
          setRankProgress(null);
          load();
        }
      } catch { /* keep polling */ }
    }, 1500);
    return () => clearInterval(id);
  }, [rankTaskModal, rankProgress?.running, load]);

  const openRank = (t) => {
    setRankTaskModal(t);
    setOverrideWinners(String(t.winners_count || 10));
    setOverrideMin(String(t.min_winning_score ?? 100));
    setRankProgress({ running: false });
  };

  const startRank = async () => {
    const t = rankTaskModal;
    if (!t) return;
    try {
      // Persist any config overrides first
      const patch = {};
      const w = Number(overrideWinners);
      const m = Number(overrideMin);
      if (w && w !== t.winners_count) patch.winners_count = w;
      if (m !== "" && m !== t.min_winning_score) patch.min_winning_score = m;
      if (Object.keys(patch).length > 0) {
        await afriUpdateTaskConfig(t.task_id, patch);
      }
      setRankProgress({ running: true, job: { graded: 0, total: 0, progress_percent: 0 } });
      await afriRankTask(t.task_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start ranking");
      setRankTaskModal(null);
      setRankProgress(null);
    }
  };

  const openDetail = async (t) => {
    setDetailTask(t);
    setOverrideWinners(String(t.winners_count || 10));
    setOverrideMin(String(t.min_winning_score ?? 100));
    try {
      const r = await afriTaskResults(t.task_id);
      setDetail(r);
    } catch {
      toast.error("Failed to load results");
    }
  };

  const saveDetailOverride = async () => {
    if (!detailTask) return;
    const patch = {};
    const w = Number(overrideWinners);
    const m = Number(overrideMin);
    if (w && w !== detailTask.winners_count) patch.winners_count = w;
    if (m !== "" && m !== detailTask.min_winning_score) patch.min_winning_score = m;
    if (Object.keys(patch).length === 0) {
      toast.info("Nothing to save");
      return;
    }
    setSavingOverride(true);
    try {
      const r = await afriUpdateTaskConfig(detailTask.task_id, patch);
      setDetailTask(r.task);
      const fresh = await afriTaskResults(detailTask.task_id);
      setDetail(fresh);
      toast.success("Configuration updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingOverride(false);
    }
  };

  const accept = async () => {
    if (!detailTask) return;
    setAcceptBusy(true);
    try {
      const r = await afriAcceptTask(detailTask.task_id);
      toast.success(`✓ ${r.winners_paid} winners paid ₦${r.total_payout_ngn.toLocaleString()} total`);
      setDetailTask(null); setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    } finally {
      setAcceptBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-700" />
            <h1 className="text-xl font-bold tracking-tight">AfriSelection AI</h1>
          </div>
          <p className="text-sm text-neutral-500">
            LLM-graded task submissions. Rank → review → accept & pay top winners.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-1 rounded-lg border border-neutral-200 bg-neutral-50 p-1"
           data-testid="afri-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 min-w-[120px] rounded-md px-3 py-1.5 text-sm font-medium transition ${
              tab === t.key ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500 hover:text-neutral-800"
            }`}
            data-testid={`afri-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-neutral-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : tasks.length === 0 ? (
        <Card><CardContent className="py-14 text-center text-sm text-neutral-500">
          No tasks in this state.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => {
            const isOpen = t.status === "open";
            const isLocked = t.status === "locked" || t.status === "awaiting_ranking";
            const cap = t.entry_cap || t.applicant_cap || 0;
            return (
            <Card key={t.task_id} data-testid={`afri-task-${t.task_id}`}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-semibold">{t.title}</p>
                    <Badge variant="outline" className="text-[10px]">{t.task_type}</Badge>
                    {isOpen && (
                      <span
                        className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
                        data-testid={`afri-pill-open-${t.task_id}`}
                        title="Round is still accepting entries"
                      >
                        Still open
                      </span>
                    )}
                    {isLocked && (
                      <span
                        className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-600 ring-1 ring-inset ring-neutral-200"
                        data-testid={`afri-pill-locked-${t.task_id}`}
                        title="No more entries — ready to rank"
                      >
                        Locked
                      </span>
                    )}
                  </div>
                  <p
                    className="mt-1 font-mono text-[10px] text-neutral-400"
                    data-testid={`afri-taskid-${t.task_id}`}
                  >
                    {t.task_id}
                  </p>
                  <div className="mt-1.5 flex flex-col gap-0.5 text-xs text-neutral-600">
                    <span>
                      <Users2 className="mr-0.5 inline h-3 w-3" />{" "}
                      <span className="font-medium tabular-nums text-neutral-900">
                        {t.entered_count}
                      </span>
                      {" / "}
                      <span className="tabular-nums">{cap}</span> entered
                    </span>
                    <span data-testid={`afri-submitted-${t.task_id}`}>
                      <span className="font-medium tabular-nums text-neutral-900">
                        {t.submitted_count || 0}
                      </span>{" "}
                      submitted
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-4 text-xs text-neutral-500">
                    <span>Winners: {t.winners_count} × {t.winner_reward_points} pts (₦{(t.winner_reward_points || 0) * 10})</span>
                    <span>Min score: {t.min_winning_score ?? 100}</span>
                    {t.ai_avg_score != null && (
                      <span className="font-medium text-amber-700">
                        Avg score: {t.ai_avg_score} · Top: {t.ai_top_score}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {t.status === "ranked" ? (
                    <Button size="sm" onClick={() => openDetail(t)}
                            className="bg-amber-700 hover:bg-amber-800"
                            data-testid={`afri-view-${t.task_id}`}>
                      View & accept <ChevronRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  ) : t.status === "finalized" ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      Paid {t.winners_paid_count || 0} ₦{(t.total_payout_ngn || 0).toLocaleString()}
                    </Badge>
                  ) : (
                    <>
                      {isOpen && (t.submitted_count || 0) > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setLockTarget(t)}
                          data-testid={`afri-lock-${t.task_id}`}
                        >
                          <Lock className="mr-1.5 h-3.5 w-3.5" /> Lock now
                        </Button>
                      )}
                      <Button size="sm" onClick={() => navigate(`/admin/afriselection/tasks/${t.task_id}/submissions`)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`afri-view-submissions-${t.task_id}`}>
                        <Eye className="mr-1.5 h-3.5 w-3.5" /> View submissions
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* Lock-now confirmation */}
      <AlertDialog
        open={!!lockTarget}
        onOpenChange={(v) => !v && !lockBusy && setLockTarget(null)}
      >
        <AlertDialogContent data-testid="afri-lock-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Lock this round?</AlertDialogTitle>
            <AlertDialogDescription>
              {lockTarget && (
                <>
                  <span className="font-medium text-neutral-900">{lockTarget.title}</span>
                  <br />
                  No more entries will be accepted. Currently{" "}
                  <b>{lockTarget.entered_count}/{lockTarget.entry_cap || lockTarget.applicant_cap}</b> entered,{" "}
                  <b>{lockTarget.submitted_count || 0}</b> submitted. You can rank the round right after locking.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={lockBusy} data-testid="afri-lock-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={lockBusy}
              onClick={async () => {
                if (!lockTarget) return;
                setLockBusy(true);
                try {
                  await afriLockTask(lockTarget.task_id);
                  toast.success(`Locked — "${lockTarget.title}" is ready to rank`);
                  setLockTarget(null);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not lock task");
                } finally {
                  setLockBusy(false);
                }
              }}
              data-testid="afri-lock-confirm"
            >
              {lockBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-3.5 w-3.5" />}
              Lock now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rank modal — allows adjusting winners_count + min_winning_score BEFORE grading */}
      <Dialog open={!!rankTaskModal} onOpenChange={(v) => !v && (setRankTaskModal(null), setRankProgress(null))}>
        <DialogContent className="sm:max-w-md" data-testid="afri-rank-dialog">
          <DialogHeader>
            <DialogTitle>
              {rankProgress?.running ? "Grading in progress…" : "Confirm ranking config"}
            </DialogTitle>
          </DialogHeader>
          {!rankProgress?.running ? (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600">
                Adjust <b>winners</b> and <b>minimum score</b> before AfriSelection AI grades every submission.
                Changes persist to the task record.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Winners count</Label>
                  <Input type="number" min={1}
                         value={overrideWinners}
                         onChange={(e) => setOverrideWinners(e.target.value)}
                         data-testid="afri-rank-winners" />
                </div>
                <div>
                  <Label className="text-xs">Min winning score (0-100)</Label>
                  <Input type="number" min={0} max={100}
                         value={overrideMin}
                         onChange={(e) => setOverrideMin(e.target.value)}
                         data-testid="afri-rank-minscore" />
                </div>
              </div>
              <p className="text-[11px] text-neutral-500">
                Task reward: <b>{rankTaskModal?.winner_reward_points} pts</b>
                {" "}(₦{((rankTaskModal?.winner_reward_points || 0) * 10).toLocaleString()})
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600">
                AfriSelection AI is scoring each submission against the ground truth.
              </p>
              {rankProgress?.job && (
                <>
                  <Progress value={rankProgress.job.progress_percent || 0} className="h-2" />
                  <p className="text-xs text-neutral-500 text-center">
                    Graded {rankProgress.job.graded || 0} / {rankProgress.job.total || "?"} —
                    {" "}avg so far: {rankProgress.job.avg_score || 0}
                  </p>
                </>
              )}
            </div>
          )}
          {!rankProgress?.running && (
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRankTaskModal(null)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={startRank}
                      data-testid="afri-rank-start">
                <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Start ranking
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Results modal — allow editing winners_count + min_winning_score post-rank */}
      <Dialog open={!!detailTask}
              onOpenChange={(v) => !v && (setDetailTask(null), setDetail(null))}>
        <DialogContent className="max-w-3xl" data-testid="afri-results-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" />
              {detailTask?.title}
            </DialogTitle>
          </DialogHeader>
          {!detail ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 rounded-lg bg-neutral-50 p-3 text-xs sm:grid-cols-4">
                <div><p className="text-neutral-500">Total submissions</p><p className="font-bold">{detail.summary.total}</p></div>
                <div><p className="text-neutral-500">Avg score</p><p className="font-bold">{detail.summary.avg_score}</p></div>
                <div><p className="text-neutral-500">Would win</p>
                  <p className="font-bold">{detail.summary.would_win_count} / {detail.summary.winners_count}</p></div>
                <div><p className="text-neutral-500">Payout each</p>
                  <p className="font-bold">₦{(detail.summary.winner_reward_points * 10).toLocaleString()}</p></div>
              </div>

              {/* Adjustable overrides + save */}
              <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-amber-900">
                  Adjust ranking cutoffs
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <div>
                    <Label className="text-[11px]">Winners count</Label>
                    <Input type="number" min={1}
                           value={overrideWinners}
                           onChange={(e) => setOverrideWinners(e.target.value)}
                           data-testid="afri-results-winners" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Min winning score</Label>
                    <Input type="number" min={0} max={100}
                           value={overrideMin}
                           onChange={(e) => setOverrideMin(e.target.value)}
                           data-testid="afri-results-minscore" />
                  </div>
                  <div className="flex items-end">
                    <Button size="sm" variant="outline"
                            disabled={savingOverride}
                            onClick={saveDetailOverride}
                            data-testid="afri-results-save-config">
                      {savingOverride
                        ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        : <Save className="mr-1 h-3.5 w-3.5" />}
                      Save
                    </Button>
                  </div>
                </div>
                <p className="mt-1.5 text-[11px] text-amber-800/80">
                  Changing these updates the task record and recomputes the &quot;would-win&quot; column below.
                </p>
              </div>

              <div className="max-h-96 overflow-y-auto rounded-lg border border-neutral-200">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white text-xs uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th className="p-2 text-left">Rank</th>
                      <th className="p-2 text-left">User</th>
                      <th className="p-2 text-left">Score</th>
                      <th className="p-2 text-left">Explanation</th>
                      <th className="p-2 text-right">Payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.entries.map((e) => (
                      <tr key={e.entry_id}
                          className={`border-t border-neutral-100 ${e.would_win ? "bg-emerald-50/50" : ""}`}
                          data-testid={`afri-result-${e.entry_id}`}>
                        <td className="p-2 tabular-nums">#{e.rank}</td>
                        <td className="p-2 font-medium">{e.username}</td>
                        <td className="p-2">
                          <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-bold tabular-nums ${
                            e.ai_score === 100 ? "bg-emerald-100 text-emerald-800"
                            : e.ai_score >= detail.summary.min_winning_score ? "bg-amber-100 text-amber-900"
                            : "bg-red-50 text-red-700"
                          }`}>{e.ai_score ?? "—"}</span>
                        </td>
                        <td className="p-2 text-xs text-neutral-500">{(e.ai_explanation || "").slice(0, 100)}</td>
                        <td className="p-2 text-right text-xs font-semibold text-emerald-700">
                          {e.would_win ? `₦${(detail.summary.winner_reward_points * 10).toLocaleString()}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetailTask(null); setDetail(null); }}
                    disabled={acceptBusy}>Close</Button>
            <Button onClick={accept}
                    disabled={acceptBusy || !detail || detail.summary.would_win_count === 0}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="afri-accept-btn">
              {acceptBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          : <Trophy className="mr-1.5 h-4 w-4" />}
              Accept & pay {detail?.summary?.would_win_count || 0} winners
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
